import { useEffect } from 'react';

import { BackrsForm, FeaturedIcon } from '@backrs_ui/common-ui';

import { Box, Typography, SxProps, Theme } from '@mui/material';

import { OnboardingQuestion, ONBOARDING_CONFIGS, OnboardingFlow } from './onboarding.types';
import { OnboardingSidebar } from './OnboardingSidebar';

const firstQuestion: OnboardingQuestion = {
  title: 'What’s your role?',
  subTitle: 'This helps us personalize your experience.',
  featuredIcon: 'User01',
  onComplete: 'CONTINUE_WITHOUT_SUBMIT',
  question: {
    type: 'RADIO',
    options: [
      ...Object.keys(ONBOARDING_CONFIGS).map((key) => ({
        label: key.charAt(0).toUpperCase() + key.slice(1),
        value: key,
      })),
    ],
  },
};

type InitialQuestionProps = Readonly<{
  onComplete: (onboardingType: OnboardingFlow) => void;
  resetOnboarding: () => void;
}>;

export const InitialQuestion = ({ onComplete, resetOnboarding }: InitialQuestionProps) => {
  useEffect(() => {
    resetOnboarding();
  }, [resetOnboarding]);

  return (
    <Box sx={containerStyle}>
      <OnboardingSidebar currentQuestionIndex={0} currentFlow="START" />

      <Box sx={contentContainerStyle}>
        <Box sx={questionContainerStyle}>
          <Box sx={questionHeaderContainerStyle}>
            <FeaturedIcon iconName={'User01'} size="xl" color="Colors/Foreground/fg-secondary" />
            <Box>
              <Typography variant="display-sm-semibold" component="div">
                {firstQuestion.title}
              </Typography>
              <Typography variant="text-md-regular">{firstQuestion.subTitle}</Typography>
            </Box>
          </Box>
          <BackrsForm
            buttonLabel="Continue"
            fields={[
              {
                name: 'onboardingType',
                label: firstQuestion.title,
                type: 'text',
                rules: { required: 'Please select a role' },
                selectOptions: {
                  type: 'SINGLE',
                  options:
                    firstQuestion.question.type === 'RADIO' ? firstQuestion.question.options : [],
                },
              },
            ]}
            onSubmit={(data) => {
              if (data.onboardingType) {
                onComplete(data.onboardingType as OnboardingFlow);
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  margin: '0 auto',
  backgroundColor: backrs.colors['Colors/Base/White'],
  width: '100%',
  height: '100dvh',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
});

const contentContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  alignSelf: 'center',
  justifySelf: 'center',
  margin: 'auto',
  backgroundColor: backrs.colors['Colors/Base/White'],
  paddingX: backrs.spacing['spacing-md'],
  paddingY: {
    xs: backrs.spacing['spacing-none'],
    sm: backrs.spacing['spacing-3xl'],
  },
  borderRadius: backrs.radius['radius-md'],
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '100%',
  gap: backrs.spacing['spacing-2xl'],
  width: '100%',
  boxSizing: 'border-box',
});

const questionContainerStyle: SxProps<Theme> = {
  minWidth: { sm: '360px' },
};

const questionHeaderContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-4xl'],
  mb: backrs.spacing['spacing-4xl'],
  textAlign: 'center',
});
